/**
 * PWA Utilities for SECRET Platform
 *
 * Handles:
 * - Service worker registration and updates
 * - "Add to Home Screen" prompt
 * - Offline status detection
 * - Cache management
 * - Push notification subscriptions
 */

export interface PWAInstallPrompt {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export interface ServiceWorkerState {
  isSupported: boolean
  isRegistered: boolean
  isUpdateAvailable: boolean
  registration: ServiceWorkerRegistration | null
}

export interface OfflineStatus {
  isOnline: boolean
  lastOnline: Date | null
}

// Store install prompt for later use
let deferredPrompt: PWAInstallPrompt | null = null
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null

/**
 * Initialize PWA functionality
 */
export async function initPWA(): Promise<void> {
  console.log('[PWA] Initializing...')

  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.warn('[PWA] Service Workers not supported')
    return
  }

  try {
    // Register service worker
    serviceWorkerRegistration = await registerServiceWorker()

    // Setup install prompt
    setupInstallPrompt()

    // Setup offline detection
    setupOfflineDetection()

    // Check for updates periodically
    setupUpdateCheck()

    console.log('[PWA] Initialization complete')
  } catch (error) {
    console.error('[PWA] Initialization failed:', error)
  }
}

/**
 * Register service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    })

    console.log('[PWA] Service Worker registered:', registration.scope)

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing

      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker installed, prompt user to refresh
            notifyUpdateAvailable()
          }
        })
      }
    })

    return registration
  } catch (error) {
    console.error('[PWA] Service Worker registration failed:', error)
    return null
  }
}

/**
 * Unregister service worker (for debugging)
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (registration) {
      const success = await registration.unregister()
      console.log('[PWA] Service Worker unregistered:', success)
      return success
    }
    return false
  } catch (error) {
    console.error('[PWA] Service Worker unregister failed:', error)
    return false
  }
}

/**
 * Check service worker status
 */
export async function getServiceWorkerState(): Promise<ServiceWorkerState> {
  const isSupported = 'serviceWorker' in navigator

  if (!isSupported) {
    return {
      isSupported: false,
      isRegistered: false,
      isUpdateAvailable: false,
      registration: null
    }
  }

  const registration = await navigator.serviceWorker.getRegistration()
  const isUpdateAvailable = registration?.waiting !== null

  return {
    isSupported: true,
    isRegistered: !!registration,
    isUpdateAvailable: isUpdateAvailable || false,
    registration: registration || null
  }
}

/**
 * Setup "Add to Home Screen" prompt
 */
function setupInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent default Chrome install prompt
    e.preventDefault()

    // Store the event for later use
    deferredPrompt = e as unknown as PWAInstallPrompt

    console.log('[PWA] Install prompt available')

    // Dispatch custom event that components can listen to
    window.dispatchEvent(new CustomEvent('pwa-install-available'))
  })

  // Track if app was successfully installed
  window.addEventListener('appinstalled', () => {
    console.log('[PWA] App installed successfully')
    deferredPrompt = null

    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('pwa-installed'))
  })
}

/**
 * Show "Add to Home Screen" prompt
 */
export async function showInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) {
    console.warn('[PWA] Install prompt not available')
    return 'unavailable'
  }

  try {
    // Show the install prompt
    await deferredPrompt.prompt()

    // Wait for user's choice
    const { outcome } = await deferredPrompt.userChoice

    console.log('[PWA] User choice:', outcome)

    // Clear the deferred prompt
    if (outcome === 'accepted') {
      deferredPrompt = null
    }

    return outcome
  } catch (error) {
    console.error('[PWA] Install prompt failed:', error)
    return 'unavailable'
  }
}

/**
 * Check if app is installable
 */
export function isInstallable(): boolean {
  return deferredPrompt !== null
}

/**
 * Check if app is installed
 */
export function isAppInstalled(): boolean {
  // Check if running in standalone mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
  const isIOSStandalone = (window.navigator as any).standalone === true

  return isStandalone || isIOSStandalone
}

/**
 * Setup offline detection
 */
function setupOfflineDetection(): void {
  window.addEventListener('online', () => {
    console.log('[PWA] Connection restored')
    window.dispatchEvent(new CustomEvent('pwa-online'))
  })

  window.addEventListener('offline', () => {
    console.log('[PWA] Connection lost')
    window.dispatchEvent(new CustomEvent('pwa-offline'))
  })
}

/**
 * Get offline status
 */
export function getOfflineStatus(): OfflineStatus {
  const isOnline = navigator.onLine
  const lastOnline = isOnline ? new Date() : null

  return {
    isOnline,
    lastOnline
  }
}

/**
 * Setup update check (every 1 hour)
 */
function setupUpdateCheck(): void {
  setInterval(async () => {
    if (serviceWorkerRegistration) {
      try {
        await serviceWorkerRegistration.update()
        console.log('[PWA] Checked for updates')
      } catch (error) {
        console.error('[PWA] Update check failed:', error)
      }
    }
  }, 60 * 60 * 1000) // 1 hour
}

/**
 * Force update service worker
 */
export async function updateServiceWorker(): Promise<void> {
  if (!serviceWorkerRegistration) {
    return
  }

  const waitingWorker = serviceWorkerRegistration.waiting

  if (waitingWorker) {
    // Tell the waiting service worker to activate
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })

    // Reload the page when the new service worker is activated
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload()
    })
  }
}

/**
 * Notify user of available update
 */
function notifyUpdateAvailable(): void {
  console.log('[PWA] Update available')
  window.dispatchEvent(new CustomEvent('pwa-update-available'))
}

/**
 * Cache management
 */

export async function cacheQRCodes(codes: Array<{ qrData: string; timeWindow: number }>): Promise<void> {
  if (!serviceWorkerRegistration || !serviceWorkerRegistration.active) {
    return
  }

  serviceWorkerRegistration.active.postMessage({
    type: 'CACHE_QR_CODES',
    payload: { codes }
  })

  console.log('[PWA] Sent QR codes to cache:', codes.length)
}

export async function cacheOrderData(order: any): Promise<void> {
  if (!serviceWorkerRegistration || !serviceWorkerRegistration.active) {
    return
  }

  serviceWorkerRegistration.active.postMessage({
    type: 'CACHE_ORDER',
    payload: { order }
  })

  console.log('[PWA] Sent order to cache:', order.id)
}

export async function clearAllCaches(): Promise<void> {
  if (!serviceWorkerRegistration || !serviceWorkerRegistration.active) {
    return
  }

  serviceWorkerRegistration.active.postMessage({
    type: 'CLEAR_CACHE'
  })

  console.log('[PWA] Clearing all caches')
}

export async function getCacheSize(): Promise<Record<string, number>> {
  if (!serviceWorkerRegistration || !serviceWorkerRegistration.active) {
    return {}
  }

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel()

    messageChannel.port1.onmessage = (event) => {
      if (event.data.type === 'CACHE_SIZE') {
        resolve(event.data.payload)
      }
    }

    serviceWorkerRegistration!.active!.postMessage(
      { type: 'GET_CACHE_SIZE' },
      [messageChannel.port2]
    )
  })
}

/**
 * Push notifications
 */

export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  if (!serviceWorkerRegistration) {
    console.warn('[PWA] Service Worker not registered')
    return null
  }

  try {
    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      console.warn('[PWA] Notification permission denied')
      return null
    }

    // Subscribe to push notifications
    const subscription = await serviceWorkerRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
      )
    })

    console.log('[PWA] Push subscription:', subscription)

    return subscription
  } catch (error) {
    console.error('[PWA] Push subscription failed:', error)
    return null
  }
}

export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  if (!serviceWorkerRegistration) {
    return false
  }

  try {
    const subscription = await serviceWorkerRegistration.pushManager.getSubscription()

    if (subscription) {
      const success = await subscription.unsubscribe()
      console.log('[PWA] Unsubscribed from push:', success)
      return success
    }

    return false
  } catch (error) {
    console.error('[PWA] Unsubscribe failed:', error)
    return false
  }
}

export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!serviceWorkerRegistration) {
    return null
  }

  try {
    const subscription = await serviceWorkerRegistration.pushManager.getSubscription()
    return subscription
  } catch (error) {
    console.error('[PWA] Get subscription failed:', error)
    return null
  }
}

/**
 * Helper: Convert VAPID key to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}

/**
 * Share API (for sharing orders, events)
 */
export async function shareContent(data: {
  title?: string
  text?: string
  url?: string
}): Promise<boolean> {
  if (!navigator.share) {
    console.warn('[PWA] Web Share API not supported')
    return false
  }

  try {
    await navigator.share(data)
    console.log('[PWA] Content shared successfully')
    return true
  } catch (error) {
    if ((error as Error).name !== 'AbortError') {
      console.error('[PWA] Share failed:', error)
    }
    return false
  }
}

/**
 * Get PWA capabilities
 */
export function getPWACapabilities() {
  return {
    serviceWorker: 'serviceWorker' in navigator,
    pushNotifications: 'PushManager' in window,
    notifications: 'Notification' in window,
    installPrompt: isInstallable(),
    isInstalled: isAppInstalled(),
    share: 'share' in navigator,
    onLine: navigator.onLine,
    storage: 'storage' in navigator && 'estimate' in navigator.storage,
    backgroundSync: 'sync' in ServiceWorkerRegistration.prototype
  }
}

/**
 * Get storage estimate
 */
export async function getStorageEstimate(): Promise<{
  usage: number
  quota: number
  percentUsed: number
} | null> {
  if (!('storage' in navigator) || !('estimate' in navigator.storage)) {
    return null
  }

  try {
    const estimate = await navigator.storage.estimate()
    const usage = estimate.usage || 0
    const quota = estimate.quota || 0
    const percentUsed = quota > 0 ? (usage / quota) * 100 : 0

    return {
      usage,
      quota,
      percentUsed
    }
  } catch (error) {
    console.error('[PWA] Storage estimate failed:', error)
    return null
  }
}
